/**
 * Shared layout for DataTable controls so table consumers follow Meridian's
 * two-row action-bar pattern while retaining ownership of behavior and content.
 * Owns the filter popover chrome (icon button, open/close state, clear button)
 * so consumers only provide the filter controls as children.
 */
import React, { useRef, useState } from 'react';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Icon from '@amzn/meridian/icon';
import Popover from '@amzn/meridian/popover';
import Row from '@amzn/meridian/row';
import SearchField from '@amzn/meridian/search-field';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';
import filterIconTokens from '@amzn/meridian-tokens/base/icon/filter';
import { useTranslation } from 'src/arb-wrapper';

export interface DataTableActionBarProps {
  /** Search field config */
  search?: {
    value: string;
    onChange: (value: string) => void;
    onSubmit?: () => void;
    placeholder?: string;
    label?: string;
    testId?: string;
    width?: number;
  };
  /**
   * @deprecated Use filterPopoverContent + onClearFilters instead.
   * Raw ReactNode for the filter button area (legacy — used by CampaignListActionBar).
   */
  filterButton?: React.ReactNode;
  /** Filter controls rendered inside the shared popover. Mutually exclusive with filterButton. */
  filterPopoverContent?: React.ReactNode;
  /** Called when "Clear Filters" is clicked inside the popover. Required when filterPopoverContent is provided. */
  onClearFilters?: () => void;
  /** Accessible label for the filter button (defaults to 'Filters') */
  filterButtonLabel?: string;
  /** Tooltip for the filter button; omitted to preserve existing callers. */
  filterButtonTooltip?: string;
  /** data-testid for the filter button */
  filterButtonTestId?: string;
  /** Content rendered after the filter button in the left row (e.g. view mode toggle) */
  trailingFilterContent?: React.ReactNode;
  /** Action buttons on the right side of Row 1 (e.g. Create Campaign, Share) */
  actions?: React.ReactNode;
  /** Content for the left side of Row 2 — filter tags, "Add filter" link, etc. */
  filterContent?: React.ReactNode;
  /** Content for the right side of Row 2 — bulk actions or placeholder text */
  bulkContent?: React.ReactNode;
}

export const DataTableActionBar: React.FC<DataTableActionBarProps> = ({
  search,
  filterButton,
  filterPopoverContent,
  onClearFilters,
  filterButtonLabel,
  filterButtonTestId,
  filterButtonTooltip,
  trailingFilterContent,
  actions,
  filterContent,
  bulkContent,
}) => {
  const { t } = useTranslation();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterAnchorRef = useRef<HTMLSpanElement>(null);
  const filterPopoverButton = (
    <Button
      aria-expanded={isFilterOpen}
      aria-haspopup="dialog"
      aria-label={filterButtonLabel ?? t('select_filters')}
      data-testid={filterButtonTestId ?? 'filter-popover-button'}
      type="icon"
      onClick={() => { setIsFilterOpen((open) => !open); }}
    >
      <Icon tokens={filterIconTokens}>{filterButtonLabel ?? t('select_filters')}</Icon>
    </Button>
  );

  return (
    <Column data-testid="data-table-action-bar" spacing="300" type="outline">
      <Row
        alignmentHorizontal="justify"
        spacingInset="300 none"
        width="100%"
        widths="fit"
      >
        <Row spacing="200" spacingInset="none 300 none">
          {search && (
            <SearchField
              data-testid={search.testId}
              label={search.label}
              placeholder={search.placeholder}
              value={search.value}
              width={search.width ?? 180}
              onChange={search.onChange}
              onSubmit={search.onSubmit ?? (() => undefined)}
            />
          )}
          {filterButton}
          {filterPopoverContent && (
            <>
              <span ref={filterAnchorRef} style={{ display: 'inline-flex' }}>
                {filterButtonTooltip
                  ? (
                  <Tooltip position="top" title={filterButtonTooltip}>
                    {filterPopoverButton}
                  </Tooltip>
                    )
                  : filterPopoverButton}
              </span>
              <Popover
                alignment="start"
                anchorNode={filterAnchorRef.current ?? undefined}
                open={isFilterOpen}
                position="bottom"
                onClose={() => { setIsFilterOpen(false); }}
              >
                <Column maxHeight="70vh" minWidth="280px" overflowY="auto" spacing="300" spacingInset="400">
                  <Text tag="span" type="b300"><b>{t('select_filters')}</b></Text>
                  {filterPopoverContent}
                  {onClearFilters && (
                    <Button
                      data-testid="popover-clear-filters"
                      type="link"
                      onClick={() => { onClearFilters(); setIsFilterOpen(false); }}
                    >
                      {t('clear_filters')}
                    </Button>
                  )}
                </Column>
              </Popover>
            </>
          )}
          {trailingFilterContent}
        </Row>
        <Row spacing="200" spacingInset="none 300 none">
          {actions}
        </Row>
      </Row>
      <Divider />
      <Row
        alignmentHorizontal="justify"
        minHeight="56px"
        spacingInset="300 none"
        width="100%"
        wrap="down"
      >
        <Row spacing="200" spacingInset="none 300 none" wrap="down">
          {filterContent}
        </Row>
        <Row spacing="200" spacingInset="none 300 none">
          {bulkContent}
        </Row>
      </Row>
    </Column>
  );
};
